/**
 * Utility functions for Google Analytics tracking
 */
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';

/**
 * Check if a tracking ID is in the old Universal Analytics format (UA-XXXXXX-Y)
 * @param {string} trackingId - The tracking ID to check
 * @returns {boolean} - True if it's a UA tracking ID, false if it's GA4
 */
export const isOldGA = (trackingId) => {
  if (!trackingId || typeof trackingId !== 'string') {
    return false;
  }
  // UA tracking IDs start with "UA-" followed by numbers and a dash, then more numbers
  return /^UA-\d+-\d+$/.test(trackingId);
};

/**
 * Check if a tracking ID is in the new GA4 format (G-XXXXXXXX)
 * @param {string} trackingId - The tracking ID to check
 * @returns {boolean} - True if it's a GA4 tracking ID, false if it's UA
 */
export const isGA4 = (trackingId) => {
  if (!trackingId || typeof trackingId !== 'string') {
    return false;
  }
  // GA4 tracking IDs start with "G-" followed by alphanumeric characters
  return /^G-[A-Z0-9]+$/.test(trackingId);
};

/**
 * Get the tracking type from a tracking ID
 * @param {string} trackingId - The tracking ID to check
 * @returns {string} - 'ua' for Universal Analytics, 'ga4' for GA4, or 'unknown'
 */
export const getTrackingType = (trackingId) => {
  if (isOldGA(trackingId)) {
    return 'ua';
  }
  if (isGA4(trackingId)) {
    return 'ga4';
  }
  return 'unknown';
};

/**
 * Track an event with both UA and GA4 if available
 * @param {string} action - The action name (e.g., 'click')
 * @param {string} category - The event category (e.g., 'College Link')
 * @param {string} label - The event label (e.g., website URL)
 * @param {string} value - Optional value for the event
 * @param {string} trackingId - The tracking ID to determine which type to use
 */
export const trackEvent = (
  action,
  category,
  label,
  value = null,
  trackingId = null,
) => {
  const trackingType = trackingId ? getTrackingType(trackingId) : null;

  // Track with Universal Analytics if available
  if (!trackingType || trackingType === 'ua') {
    try {
      if (value) {
        ReactGA.event({
          category,
          action,
          label,
          value: parseInt(value, 10),
        });
      } else {
        ReactGA.event({
          category,
          action,
          label,
        });
      }
    } catch (error) {
      console.warn('ReactGA tracking failed:', error);
    }
  }

  // Track with GA4 if available
  if (!trackingType || trackingType === 'ga4') {
    try {
      const eventData = {
        category,
        label,
      };

      if (value) {
        eventData.value = parseInt(value, 10);
      }

      ReactGA4.event(action, eventData);
    } catch (error) {
      console.warn('ReactGA4 tracking failed:', error);
    }
  }
};



// WEBPACK FOOTER //
// ./src/client/utils/analytics.js